<script lang="ts">
	import type { UtterancePayloadItem } from '@repo/commons/models'
	import { Button, ButtonType, Icon } from '@repo/commons/components'
	import { renderMarkdown, parseUtterance } from '../MarkdownInput/markdownInputUtils.ts'
	import ChatImage from './ChatImage.svelte'

	interface Props {
		utterance: string
		utterancePayload?: Record<string, UtterancePayloadItem>
		onComponentClick?:
			| ((id: string, componentType: string, props: Record<string, any>) => void)
			| null
		onImageLoad?: ((imageFileName: string) => Promise<Blob | string>) | null
		onOpenImageModal?: ((imageUrl: string) => void) | null
		processImages?: boolean
	}

	let {
		utterance,
		utterancePayload = {},
		onComponentClick,
		onImageLoad = null,
		onOpenImageModal = null,
		processImages = false
	}: Props = $props()

	interface MessageSegment {
		type: 'text' | 'image' | 'placeholder'
		content: string
		key?: string
	}

	let messageSegments: MessageSegment[] = $state([])
	const imageCache: Map<string, string> = new Map()
	const pendingImages: Set<string> = new Set() // Track images currently being loaded

	function handleComponentClick(key: string, payloadItem: UtterancePayloadItem) {
		onComponentClick?.(payloadItem.props.id, payloadItem.component, payloadItem.props)
	}

	const segments = $derived.by(() => {
		if (!processImages) {
			return parseUtterance(utterance)
		}
		return messageSegments.length > 0 ? messageSegments : parseUtterance(utterance)
	})

	async function processMessageWithImages() {
		if (!processImages || !onImageLoad) {
			return
		}

		const imageRegex = /!\[.*?\]\((.*?)\)/g
		let lastIndex = 0
		const segments: MessageSegment[] = []

		for (const match of utterance.matchAll(imageRegex)) {
			const imageFileName = match[1]
			const textSegment = utterance.slice(lastIndex, match.index)

			// Add text segment if any
			if (textSegment) {
				segments.push({
					type: 'text',
					content: textSegment
				})
			}

			let imageUrl: string | undefined

			// Check cache first
			if (imageCache.has(imageFileName)) {
				imageUrl = imageCache.get(imageFileName)
			}
			// Skip if already loading (prevent duplicate API calls)
			else if (pendingImages.has(imageFileName)) {
				// Image is currently being loaded, skip for now
				// It will be added to segments on the next render after loading completes
			}
			// Load the image
			else {
				pendingImages.add(imageFileName) // Mark as loading
				try {
					const result = await onImageLoad(imageFileName)
					// Check if result is a Blob or a string (presigned URL)
					if (typeof result === 'string') {
						// Direct URL (e.g., presigned URL from PDF image endpoint)
						imageUrl = result
					} else {
						// Blob (e.g., from legacy image endpoint)
						imageUrl = URL.createObjectURL(result)
					}
					imageCache.set(imageFileName, imageUrl)

					// Re-process to add the loaded image to segments
					processMessageWithImages()
				} catch (error) {
					console.error(`Failed to load image: ${imageFileName}`, error)
				} finally {
					pendingImages.delete(imageFileName) // Remove from pending
				}
			}

			// Add image segment if the URL is valid
			if (imageUrl) {
				segments.push({
					type: 'image',
					content: imageUrl
				})
			}

			lastIndex = match.index! + match[0].length
		}

		// Add any remaining text after the last image
		if (lastIndex < utterance.length) {
			segments.push({
				type: 'text',
				content: utterance.slice(lastIndex)
			})
		}

		messageSegments = segments
	}

	// React to utterance changes
	$effect(() => {
		if (processImages) {
			processMessageWithImages()
		}
	})
</script>

<div class="template-renderer">
	{#each segments as segment (segment.key || segment.content)}
		{#if segment.type === 'text'}
			<div class="text-segment">
				{@html renderMarkdown(segment.content)}
			</div>
		{:else if segment.type === 'image'}
			<ChatImage src={segment.content} alt="Chat image" onOpenModal={onOpenImageModal} />
		{:else if segment.type === 'placeholder' && segment.key}
			{@const payloadItem = utterancePayload[segment.key]}
			{#if payloadItem}
				{#if payloadItem.component === 'IconedLink'}
					<Button
						buttonText={payloadItem.props.text || payloadItem.props.id}
						buttonType={ButtonType.CUSTOM}
						onclick={() => handleComponentClick(segment.key!, payloadItem)}
						icon={payloadItem.props.icon ? Icon : null}
						iconProps={{ name: payloadItem.props.icon, size: 'md', class: 'text-[#1A74A8]' }}
					/>
				{:else if payloadItem.component === 'CopilotLink'}
					<Button
						buttonText={payloadItem.props.text || payloadItem.props.id}
						buttonType={ButtonType.CUSTOM}
						onclick={() => handleComponentClick(segment.key!, payloadItem)}
						icon={payloadItem.props.icon ? Icon : null}
						iconProps={{ name: payloadItem.props.icon, size: 'md', class: 'text-[#1A74A8]' }}
					/>
				{:else}
					<span class="unknown-component">{payloadItem.props.text || payloadItem.props.id}</span>
				{/if}
			{:else}
				<span class="missing-placeholder">{segment.content}</span>
			{/if}
		{/if}
	{/each}
</div>

<style lang="postcss">
	.template-renderer {
		@apply inline;
	}

	.text-segment {
		@apply text-sm text-gray-700 leading-relaxed break-words;
	}

	.text-segment :global(h1) {
		@apply text-xl font-bold mb-2;
	}

	.text-segment :global(h2) {
		@apply text-lg font-semibold mb-2;
	}

	.text-segment :global(h3) {
		@apply text-base font-semibold mb-1;
	}

	.text-segment :global(p) {
		@apply mb-2;
	}

	.text-segment :global(ul),
	.text-segment :global(ol) {
		@apply flex flex-col gap-1 m-0 p-0 pl-6 mb-3;
	}

	.text-segment :global(li) {
		@apply m-0;
	}

	.text-segment :global(li > p) {
		@apply m-0;
	}

	.text-segment :global(ul > li) {
		list-style-type: disc;
	}

	.text-segment :global(blockquote) {
		@apply border-l-4 border-gray-300 pl-4 italic text-gray-600 mb-2;
	}

	.text-segment :global(strong) {
		@apply font-semibold;
	}

	.text-segment :global(em) {
		@apply italic;
	}

	.text-segment :global(del) {
		@apply line-through text-gray-500;
	}

	.text-segment :global(a) {
		@apply text-blue-600 underline hover:text-blue-800;
	}
	.missing-placeholder {
		@apply text-red-500 bg-red-50 px-2 py-1 rounded border border-red-200;
	}

	.unknown-component {
		@apply text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-200;
	}

	.template-renderer :global(.button-class.custom) {
		height: unset;
		display: inline;
	}

	.template-renderer :global(.button-class .inner-container) {
		gap: unset;
		display: block;
	}

	.template-renderer :global(.button-class .inner-container .icon-wrapper) {
		position: relative;
		top: 7px;
	}

	.template-renderer :global(.button-class .inner-container .button-text) {
		@apply text-[#1A74A8];
	}
</style>
